import { Col, Container, Row } from 'react-bootstrap';
import React, { useContext, useEffect, useState } from 'react';

import { Card } from '../../../../../components/card';
import { GameContext } from '../../../../../contexts/game';
import { TeamContext } from '../../../../../contexts/team';
import { PlayerContext } from '../../../../../contexts/player';
import { CardList } from '../../../../../components/card-list';
import { GameDocument } from '../../../../../types/game-document';
import { GetAsset } from '../../../../../utils/game-document/assets';
import SearchInput from '../../../../../components/input/search-input';
import { Title } from '../../../../../types/game-document/entities/title';
import { PlayerStateFull, TeamStateFull } from '../../../../../types/state';
import { GameDocumentContext } from '../../../../../contexts/game-document';
import { EnterGameButton } from '../../../../../components/enter-game-button';
import { GetResourceValue } from '../../../../../utils/game-document/resources';
import { DisplayLanguageContext } from '../../../../../contexts/display-languages';
import AchievementDialog from '../../../../../components/dialog/achievement-dialog';
import { generateTitleById } from '../../../../../utils/game-document/display-languages';
import { OverlayContext } from '../../../../../contexts/overlay';

interface TitleAchievement extends Title {
  titleRes: string;
  imageRes: string;
  summaryRes: string;
}

/**
 * Loads the Title's resources and combines the result.
 * @param gameDocument
 * @param language
 * @param titleId
 */
export const GetTitleAchievement = (
  gameDocument: GameDocument,
  language: string,
  titleId: string
) => {
  let titleAsset = GetAsset<Title>(
    gameDocument.assets.titles!,
    titleId
  ) as TitleAchievement;
  titleAsset.titleRes = GetResourceValue(
    gameDocument,
    titleAsset.titleResId!,
    language
  );
  titleAsset.imageRes = GetResourceValue(
    gameDocument,
    titleAsset.imageResId!,
    language
  );
  titleAsset.summaryRes = GetResourceValue(
    gameDocument,
    titleAsset.summaryResId!,
    language
  );
  return titleAsset;
};

/**
 * Converts the Player's Titles into TitleAchievements
 * @param gameDocument
 * @param playerState
 */
const CastPlayerTitles = (
  gameDocument: GameDocument,
  playerState: PlayerStateFull
) =>
  playerState.titles!.map((title, index) =>
    GetTitleAchievement(gameDocument, playerState.language?.name! ?? '', title)
  ) ?? [];

/**
 * Converts the Player's Titles into TitleAchievements
 * @param gameDocument
 * @param teamState
 */
const CastTeamTitles = (gameDocument: GameDocument, teamState: TeamStateFull) =>
  (teamState.titles ?? []).map((title, index) =>
    GetTitleAchievement(gameDocument, '', title)
  ) ?? [];

/**
 * Search the list of TitleAchievements for the provided search term.
 * @param titles
 * @param searchTerm
 */
const SearchTitleAchievement = (
  titles: TitleAchievement[],
  searchTerm: string
) =>
  titles.filter((i) =>
    i.titleRes.toLowerCase().includes(searchTerm.toLowerCase())
  );

export const Roles = () => {
  const [game] = useContext(GameContext);
  const [gameDocument] = useContext(GameDocumentContext);
  const [teamState] = useContext(TeamContext);
  const [playerState] = useContext(PlayerContext);
  const [displayLanguageContext] = useContext(DisplayLanguageContext);

  const [titleCard, setTitleCard] = useState<TitleAchievement | undefined>();
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [playerTitles, setPlayerTitles] = useState<TitleAchievement[]>([]);
  const [teamTitles, setTeamTitles] = useState<TitleAchievement[]>([]);

  const inGameSetting =
    game.gameState?.runtime?.inGame ??
    gameDocument.gameDocument?.settings.inGame;

  const showTeamsTab = inGameSetting?.showInGameTeams;

  // When the player's titles or search term changes. Load titles.
  useEffect(() => {
    setPlayerTitles(
      SearchTitleAchievement(
        CastPlayerTitles(gameDocument.gameDocument!, playerState.playerState!),
        searchTerm
      )
    );
  }, [playerState.playerState?.titles, searchTerm]);

  // When the team's titles or search term changes. Load titles.
  useEffect(() => {
    if (!teamState.teamState) return;

    setTeamTitles(
      SearchTitleAchievement(
        CastTeamTitles(gameDocument.gameDocument!, teamState.teamState!),
        searchTerm
      )
    );
  }, [teamState.teamState, searchTerm]);

  return (
    <>
      <Container className={'drawer__body'} fluid>
        <Row className={'mb-4 align-items-center border-bottom pb-3'}>
          <Col>
            <h1 className={'m-0 fw-bold'}>
              {generateTitleById(
                '832f9ead-d125-4418-aebb-ebdff00a3a8d',
                gameDocument,
                displayLanguageContext.displayLanguageSelected.resources!,
                'game'
              ) || 'Roles'}
            </h1>
          </Col>
          <Col xs={'auto'}>
            <SearchInput
              placeholder={
                generateTitleById(
                  '9a3b4c5d-6e7f-8a9b-0c1d-2e3f4a5b6c7d',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Search roles'
              }
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.value)}
            />
          </Col>
        </Row>

        {playerTitles.length > 0 && (
          <Row>
            <Col xs={12} className={'px-4 pb-4'}>
              <Col xs={12}>
                <div>
                  {generateTitleById(
                    'f932f99f-2e5f-4b02-9977-ba9a58764a8e',
                    gameDocument,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Player titles'}
                </div>
              </Col>
              <Col xs={12}>
                <CardList>
                  {playerTitles &&
                    playerTitles
                      .filter((item) => !item.hideInGame)
                      .map((title, index) => {
                        return (
                          <Card
                            key={index}
                            imageUrl={title.imageRes}
                            title={title.titleRes}
                            subTitle={''}
                            onClick={() => setTitleCard(title)}></Card>
                        );
                      })}
                </CardList>
              </Col>
            </Col>
          </Row>
        )}

        {showTeamsTab && teamTitles.length > 0 && (
          <Row>
            <Col xs={12} className={'px-4 pb-4'}>
              <Col xs={12}>
                <div>
                  {generateTitleById(
                    '5c5b9991-689f-459d-99b9-0453d313f8c9',
                    gameDocument,
                    displayLanguageContext.displayLanguageSelected.resources!,
                    'game'
                  ) || 'Team titles'}
                </div>
              </Col>
              <Col xs={12}>
                <CardList>
                  {teamTitles &&
                    teamTitles
                      .filter((item) => !item.hideInGame)
                      .map((title, index) => {
                        return (
                          <Card
                            key={index}
                            imageUrl={title.imageRes}
                            title={title.titleRes}
                            subTitle={''}
                            onClick={() => setTitleCard(title)}></Card>
                        );
                      })}
                </CardList>
              </Col>
            </Col>
          </Row>
        )}

        <Row>
          <Col xs={12} className={'px-4'}>
            <Col xs={12}>
              <div>
                {generateTitleById(
                  'cf3a5e89-6556-4671-9593-0f930e29c58d',
                  gameDocument,
                  displayLanguageContext.displayLanguageSelected.resources!,
                  'game'
                ) || 'Available titles'}
              </div>
            </Col>
            <Col xs={12}>
              <CardList>
                {gameDocument.gameDocument?.assets
                  .titles!.filter((title) => !title.hideInGame)
                  .map((title, index) => {
                    let titleAsset = GetTitleAchievement(
                      gameDocument.gameDocument!,
                      playerState?.playerState?.language?.name ?? '',
                      title.id
                    );
                    return (
                      <Card
                        key={index}
                        imageUrl={titleAsset.imageRes}
                        title={titleAsset.titleRes ?? ''}
                        subTitle={''}
                        onClick={() => setTitleCard(titleAsset)}></Card>
                    );
                  })}
              </CardList>
            </Col>
          </Col>
        </Row>
      </Container>
      <EnterGameButton />

      {titleCard && (
        <AchievementDialog
          imageUrl={titleCard.imageRes}
          onMaskClick={() => setTitleCard(undefined)}
          onClose={() => setTitleCard(undefined)}>
          <h1>Role</h1>
          <h2>{titleCard.titleRes}</h2>
          <p>{titleCard.summaryRes}</p>
        </AchievementDialog>
      )}
    </>
  );
};
